<script setup>
import { ref, reactive } from "vue";
import { validateEmail } from "@/utils/validation";
import axios from "axios";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import { Checkbox, useRecaptchaProvider } from "vue-recaptcha";
import ContactSupportDialog from "@/components/dialogs/ContactSupportDialog.vue";

useRecaptchaProvider();

const email = ref("");
const errors = reactive({
  email: "",
  google_recaptcha_response: "",
});
const errorMessage = ref("");
const isLoading = ref(false);
const $t = useI18n().t;
const google_recaptcha_response = ref("");
const recaptchaAttempt = ref(0);

const isValid = () => {
  errors.google_recaptcha_response =
    google_recaptcha_response.value === "" ? "Please check the reCAPTCHA checkbox" : "";
  errors.email = validateEmail(email.value);

  return Object.values(errors).every((error) => error === "");
};

const handleSubmit = async () => {
  if (isValid()) {
    isLoading.value = true;

    try {
      const response = await axios.post("api/forgot-password", {
        email: email.value,
        google_recaptcha_response: google_recaptcha_response.value,
      });

      if (response.status === 200) {
        useToast().success($t("notifications.forgot_password_success"));
        email.value = "";
        errorMessage.value = "";
        // Reset reCAPTCHA for potential subsequent submissions
        google_recaptcha_response.value = "";
        recaptchaAttempt.value++;
      } else {
        useToast().error($t("notifications.default_error"));
      }
    } catch (error) {
      errorMessage.value = error.response.data.message;
      useToast().error(errorMessage.value);
    } finally {
      isLoading.value = false;
    }
  }
};

const isContactSupportDialogOpen = ref(false);

const showContactSupportDialog = () => {
  isContactSupportDialogOpen.value = true;
};
</script>

<template>
  <div class="auth-container">
    <!-- Logo Section -->
    <div class="logo-section">
      <router-link to="/login">
        <img
          src="/images/logos/legalTorchLogo.svg"
          alt="Legal Torch Logo"
          class="logo clickable"
        />
      </router-link>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <div class="main-content">
        <h2 class="form-title">{{ $t("auth.forgot_password") }}</h2>
        <p class="form-subtitle">
          {{ $t("auth.forgot_password_subtitle") }}
        </p>

        <form @submit.prevent="handleSubmit">
          <!-- Email Input -->
          <div class="input-group">
            <label class="input-label">{{ $t("common.email") }}*</label>
            <input
              v-model="email"
              type="email"
              class="form-input"
              :placeholder="$t('common.email')"
              :class="{ error: errors.email || errorMessage }"
              autocomplete="email"
            />
            <span v-if="errors.email || errorMessage" class="error-text">
              {{ errors.email || errorMessage }}
            </span>
          </div>

          <!-- Recaptcha -->
          <div class="recaptcha-container">
            <Checkbox :key="recaptchaAttempt" v-model="google_recaptcha_response" />
            <span v-if="errors.google_recaptcha_response" class="error-text">
              {{ $t("auth.recaptcha") }}
            </span>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="submit-btn"
            :disabled="!email || !google_recaptcha_response || isLoading"
            :class="{ loading: isLoading }"
          >
            <div v-if="isLoading" class="loading-content">
              <div class="spinner"></div>
            </div>
            <span v-else>{{ $t("auth.reset_password") }}</span>
          </button>

          <!-- Bottom Links -->
          <div class="bottom-links">
            <router-link to="/login" class="link">
              {{ $t("auth.remember_the_password") }}
            </router-link>
            <button type="button" class="support-link" @click="showContactSupportDialog">
              {{ $t("auth.need_help") }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <ContactSupportDialog
      :is-dialog-open="isContactSupportDialogOpen"
      @close-dialog="isContactSupportDialogOpen = false"
    />
  </div>
</template>
<style scoped lang="scss">
.auth-container {
  min-height: 100vh;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.logo-section {
  margin-bottom: 40px;
}

.logo {
  width: 256px;
  height: 23px;

  &.clickable {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }
}

.content-wrapper {
  width: 100%;
  max-width: 430px;
  display: flex;
  justify-content: center;
}

.main-content {
  flex: 1;
  width: 100%;
  max-width: 430px;
}

.form-title {
  text-align: center;
  font-size: 40px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
  line-height: 1.2;
}

.form-subtitle {
  text-align: center;
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 1.5;
}

.input-group {
  margin-bottom: 16px;
}

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 16px 16px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  font-size: 16px;
  transition: border-color 0.2s;
  height: 48px;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: none;
  }

  &.error {
    border-color: #ef4444;
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.recaptcha-container {
  margin-bottom: 20px;
}

.submit-btn {
  width: 100%;
  background-color: #d8ed84;
  color: #1f2937;
  border: none;
  border-radius: 16px;
  padding: 16px 14px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: 20px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #c9e16f;
  }

  &:disabled {
    background-color: rgba(216, 237, 132, 0.4);
    color: rgba(31, 41, 55, 0.6);
    cursor: not-allowed;
    box-shadow: 0 0 0 1px rgba(216, 237, 132, 0.2);
  }

  &.loading {
    background-color: rgba(216, 237, 132, 0.4);
    color: rgba(31, 41, 55, 0.6);
  }

  .loading-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid transparent;
    border-top: 2px solid #9ca3af;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    flex-shrink: 0;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.bottom-links {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: center;
}

.link {
  color: #2563eb;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
}

.support-link {
  background: none;
  border: none;
  color: #2563eb;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
}

@media (max-width: 768px) {
  .auth-container {
    padding: 24px 16px;
  }

  .logo {
    width: 200px;
    height: 18px;
  }

  .tabs-container {
    min-width: auto;
    width: 100%;
    max-width: 450px;
  }

  .content-wrapper {
    width: 100%;
    max-width: 100%;
  }

  .main-content {
    width: 100%;
    max-width: 100%;
  }

  .form-title {
    font-size: 28px;
  }

  .form-subtitle {
    font-size: 14px;
  }
}
</style>
